import { useCallback, useEffect, useRef, useState } from "react";
import type { Citation, FieldValues, OutputKind } from "./types";

/**
 * The run client + hook.
 *
 * `src/lib/api.ts` is owned elsewhere and may not exist yet, so this file
 * declares the interface it needs and ships a local `fetch` fallback against
 * the documented endpoints. Nothing here branches on a field id.
 *
 * Every response is normalized defensively: the server is the authority on
 * shape, and a field the server renames must degrade to a missing value, not
 * a white screen.
 */

// ─── Wire types ───

export interface QuotaState {
  used: number;
  limit: number;
  remaining: number;
  /** True while the caller has no account — the 3-runs-a-day tier. */
  anonymous: boolean;
  resetsAt?: string;
}

export type RunStatus = "ok" | "refused";

export interface RunResult {
  id: string;
  fieldId: string;
  /** 'refused' is a CORRECT outcome (e.g. Law found no statute). Never an error. */
  status: RunStatus;
  /** The model output, or the refusal message when status is 'refused'. */
  output: string;
  outputKind?: OutputKind;
  citations?: Citation[];
  /** Values resolved by autodetect pre-calls, keyed by input name. */
  detected?: Record<string, string>;
  /** True when no GEMINI_API_KEY is configured — output is canned. */
  isDemo: boolean;
  createdAt: string;
  quota?: QuotaState;
  /** Echoed inputs, so history can re-open the form pre-filled. */
  values?: FieldValues;
}

export interface HistoryItem extends RunResult {
  fieldTitle?: string;
}

export interface CoverageEntry {
  /** Matches a field pack's domain option value where applicable. */
  key: string;
  label: string;
  /** How many statutes/articles we hold for this category. 0 = not ingested. */
  documents: number;
  articles?: number;
  lastIngested?: string;
  /** Explicitly false when we hold nothing. Coverage is shown, never implied. */
  covered: boolean;
  notes?: string;
}

export interface CoverageResponse {
  corpus: string;
  updatedAt?: string;
  source?: string;
  sourceUrl?: string;
  entries: CoverageEntry[];
}

export interface SessionUser {
  id: string;
  phone: string;
}

export interface SessionState {
  user: SessionUser | null;
  quota: QuotaState | null;
}

// ─── Errors ───

export type ApiErrorCode = "quota_exhausted" | "unauthorized" | "invalid" | "server" | "network";

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  constructor(message: string, status: number, code: ApiErrorCode) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

function codeForStatus(status: number): ApiErrorCode {
  if (status === 402 || status === 429) return "quota_exhausted";
  if (status === 401) return "unauthorized";
  if (status === 400 || status === 422) return "invalid";
  return "server";
}

const MESSAGES: Record<ApiErrorCode, string> = {
  quota_exhausted: "خلصت حصتك لهذا اليوم.",
  unauthorized: "لازم تسجّل دخول أول.",
  invalid: "في خطأ بالمعلومات المرسلة.",
  server: "صار خطأ عندنا. جرّب مرة ثانية.",
  network: "ما وصلنا للخادم. تأكد من الإنترنت وجرّب مرة ثانية.",
};

// ─── Low-level fetch ───

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      credentials: "include",
      ...init,
      headers: {
        "content-type": "application/json",
        ...(init?.headers ?? {}),
      },
    });
  } catch {
    throw new ApiError(MESSAGES.network, 0, "network");
  }

  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }

  if (!res.ok) {
    const code = codeForStatus(res.status);
    const rec = isRecord(body) ? body : {};
    const msg = str(rec.error) ?? str(rec.message) ?? MESSAGES[code];
    throw new ApiError(msg, res.status, code);
  }

  return body as T;
}

// ─── Normalizers ───

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
function str(v: unknown): string | undefined {
  return typeof v === "string" ? v : undefined;
}
function num(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

export function normalizeQuota(v: unknown): QuotaState | null {
  if (!isRecord(v)) return null;
  const limit = num(v.limit) ?? num(v.total) ?? 0;
  const used = num(v.used) ?? 0;
  const remaining = num(v.remaining) ?? Math.max(0, limit - used);
  return {
    used,
    limit,
    remaining,
    anonymous: v.anonymous === true || v.anon === true,
    resetsAt: str(v.resetsAt) ?? str(v.resets_at),
  };
}

function normalizeCitation(v: unknown): Citation | null {
  if (!isRecord(v)) return null;
  const status = str(v.status);
  return {
    lawTitle: str(v.lawTitle) ?? str(v.law_title) ?? "قانون غير مسمّى",
    lawNumber: str(v.lawNumber) ?? str(v.law_number) ?? "—",
    year: str(v.year) ?? "—",
    article: str(v.article),
    // Unknown status is surfaced as غير معروف, never silently upgraded to نافذ.
    status:
      status === "نافذ" || status === "معدل" || status === "ملغي" ? status : "غير معروف",
    sourceUrl: str(v.sourceUrl) ?? str(v.source_url),
    lastFetched: str(v.lastFetched) ?? str(v.last_fetched),
  };
}

export function normalizeRun(v: unknown): RunResult {
  const r = isRecord(v) ? v : {};
  const rawStatus = str(r.status);
  const citations = Array.isArray(r.citations)
    ? r.citations.map(normalizeCitation).filter((c): c is Citation => c !== null)
    : undefined;
  const detectedRaw = isRecord(r.detected) ? r.detected : undefined;
  const detected = detectedRaw
    ? Object.fromEntries(
        Object.entries(detectedRaw).flatMap(([k, val]) => (str(val) ? [[k, String(val)]] : [])),
      )
    : undefined;

  return {
    id: str(r.id) ?? crypto.randomUUID(),
    fieldId: str(r.fieldId) ?? str(r.field_id) ?? "",
    status: rawStatus === "refused" ? "refused" : "ok",
    output: str(r.output) ?? str(r.text) ?? str(r.message) ?? "",
    outputKind: str(r.outputKind) as OutputKind | undefined,
    citations,
    detected,
    isDemo: r.isDemo === true || r.is_demo === true || r.demo === true,
    createdAt: str(r.createdAt) ?? str(r.created_at) ?? new Date().toISOString(),
    quota: normalizeQuota(r.quota) ?? undefined,
    values: isRecord(r.values) ? (r.values as FieldValues) : undefined,
  };
}

// ─── Endpoints ───

export function run(fieldId: string, values: FieldValues, signal?: AbortSignal): Promise<RunResult> {
  return apiFetch<unknown>("/api/run", {
    method: "POST",
    body: JSON.stringify({ fieldId, values }),
    signal,
  }).then(normalizeRun);
}

/** Optional cheap pre-call for `autodetect` inputs. Falls back to detect-at-run
 *  when the server doesn't offer the endpoint. */
export async function detect(
  fieldId: string,
  inputName: string,
  values: FieldValues,
): Promise<string | null> {
  try {
    const res = await apiFetch<unknown>("/api/detect", {
      method: "POST",
      body: JSON.stringify({ fieldId, input: inputName, values }),
    });
    if (!isRecord(res)) return null;
    return str(res.value) ?? str(res.detected) ?? null;
  } catch {
    return null;
  }
}

export function fetchHistory(): Promise<HistoryItem[]> {
  return apiFetch<unknown>("/api/history").then((v) => {
    const list = Array.isArray(v) ? v : isRecord(v) && Array.isArray(v.runs) ? v.runs : [];
    return list.map((item) => {
      const base = normalizeRun(item);
      const title = isRecord(item) ? str(item.fieldTitle) ?? str(item.field_title) : undefined;
      return { ...base, fieldTitle: title };
    });
  });
}

export function fetchCoverage(): Promise<CoverageResponse> {
  return apiFetch<unknown>("/api/coverage").then((v) => {
    const r = isRecord(v) ? v : {};
    const rawEntries = Array.isArray(r.entries)
      ? r.entries
      : Array.isArray(r.categories)
        ? r.categories
        : [];
    const entries: CoverageEntry[] = rawEntries.flatMap((e) => {
      if (!isRecord(e)) return [];
      const documents = num(e.documents) ?? num(e.laws) ?? num(e.count) ?? 0;
      return [
        {
          key: str(e.key) ?? str(e.domain) ?? str(e.id) ?? "unknown",
          label: str(e.label) ?? str(e.title) ?? "—",
          documents,
          articles: num(e.articles),
          lastIngested: str(e.lastIngested) ?? str(e.last_ingested) ?? str(e.lastFetched),
          covered: typeof e.covered === "boolean" ? e.covered : documents > 0,
          notes: str(e.notes),
        },
      ];
    });
    return {
      corpus: str(r.corpus) ?? "iraqi-law",
      updatedAt: str(r.updatedAt) ?? str(r.updated_at),
      source: str(r.source),
      sourceUrl: str(r.sourceUrl) ?? str(r.source_url),
      entries,
    };
  });
}

export function fetchSession(): Promise<SessionState> {
  return apiFetch<unknown>("/api/me").then((v) => {
    const r = isRecord(v) ? v : {};
    const u = isRecord(r.user) ? r.user : null;
    return {
      user: u && str(u.id) ? { id: String(u.id), phone: str(u.phone) ?? "" } : null,
      quota: normalizeQuota(r.quota),
    };
  });
}

export function requestOtp(phone: string): Promise<{ ok: boolean; devCode?: string }> {
  return apiFetch<unknown>("/api/auth/request", {
    method: "POST",
    body: JSON.stringify({ phone }),
  }).then((v) => {
    const r = isRecord(v) ? v : {};
    return { ok: r.ok !== false, devCode: str(r.devCode) ?? str(r.code) };
  });
}

export function verifyOtp(phone: string, code: string): Promise<SessionState> {
  return apiFetch<unknown>("/api/auth/verify", {
    method: "POST",
    body: JSON.stringify({ phone, code }),
  }).then((v) => {
    const r = isRecord(v) ? v : {};
    const u = isRecord(r.user) ? r.user : null;
    return {
      user: u && str(u.id) ? { id: String(u.id), phone: str(u.phone) ?? phone } : null,
      quota: normalizeQuota(r.quota),
    };
  });
}

export function signOut(): Promise<void> {
  return apiFetch<unknown>("/api/auth/logout", { method: "POST" }).then(() => undefined);
}

// ─── The hook ───

export type RunPhase = "idle" | "running" | "done" | "error";

/** These calls take 5–20s. Silence for 20 seconds reads as "it's broken", so we
 *  narrate the wait honestly instead of faking a percentage. */
const STAGES: { at: number; label: string }[] = [
  { at: 0, label: "نرسل الاستمارة…" },
  { at: 1200, label: "نقرأ جوابك ونبني التعليمات…" },
  { at: 4000, label: "النموذج يشتغل على النص…" },
  { at: 11000, label: "بعده يشتغل — النصوص الطويلة تاخذ وقت…" },
  { at: 22000, label: "طوّل بالك شوية، هاي المرحلة الأخيرة…" },
];

export interface UseRunApi {
  phase: RunPhase;
  result: RunResult | null;
  error: ApiError | null;
  /** 0→1. Approaches 1 asymptotically; only a real response completes it. */
  progress: number;
  stageLabel: string;
  elapsedMs: number;
  start: (fieldId: string, values: FieldValues) => Promise<RunResult | null>;
  reset: () => void;
  cancel: () => void;
}

export function useRun(): UseRunApi {
  const [phase, setPhase] = useState<RunPhase>("idle");
  const [result, setResult] = useState<RunResult | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<number | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      abortRef.current?.abort();
      if (timerRef.current !== null) window.clearInterval(timerRef.current);
    };
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    stopTimer();
    setPhase("idle");
    setResult(null);
    setError(null);
    setElapsedMs(0);
  }, [stopTimer]);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    stopTimer();
    setPhase("idle");
    setElapsedMs(0);
  }, [stopTimer]);

  const start = useCallback(
    async (fieldId: string, values: FieldValues): Promise<RunResult | null> => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      setPhase("running");
      setError(null);
      setResult(null);
      setElapsedMs(0);

      const started = Date.now();
      stopTimer();
      timerRef.current = window.setInterval(() => {
        if (mounted.current) setElapsedMs(Date.now() - started);
      }, 250);

      try {
        const res = await run(fieldId, values, ctrl.signal);
        if (!mounted.current || ctrl.signal.aborted) return null;
        stopTimer();
        setResult(res);
        setPhase("done");
        return res;
      } catch (e) {
        if (!mounted.current || ctrl.signal.aborted) return null;
        stopTimer();
        const err =
          e instanceof ApiError ? e : new ApiError(MESSAGES.server, 500, "server");
        setError(err);
        setPhase("error");
        return null;
      }
    },
    [stopTimer],
  );

  // Asymptotic: 63% at 8s, ~86% at 16s, never 100% until the server answers.
  const progress = phase === "running" ? 1 - Math.exp(-elapsedMs / 8000) : phase === "done" ? 1 : 0;

  let stageLabel = STAGES[0].label;
  for (const s of STAGES) if (elapsedMs >= s.at) stageLabel = s.label;

  return { phase, result, error, progress, stageLabel, elapsedMs, start, reset, cancel };
}
